import React, {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {FiCheck, FiChevronLeft, FiChevronRight, FiEdit, FiMinus, FiTrash2} from "react-icons/fi";

const thStyle = {
    padding: "10px 12px",
    fontSize: "var(--enigoo-fs-sm)",
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    background: "#f8fafc",
    borderBottom: "2px solid #e2e8f0",
    whiteSpace: "nowrap",
    textAlign: "left",
};

const tdStyle = {
    padding: "10px 12px",
    fontSize: "var(--enigoo-fs-lg)",
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500];

// ─── shared sub-components ────────────────────────────────────────────────────

export const EmptyState = ({text = "Žádná data"}) => (
    <div style={{padding: "40px", textAlign: "center", color: "#94a3b8", fontSize: "var(--enigoo-fs-md)"}}>{text}</div>
);

export const EditIconBtn = ({to, onClick}) => {
    const btn = (
        <button
            type="button"
            onClick={onClick}
            style={{width: "28px", height: "28px", background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer"}}
        >
            <FiEdit size={13}/>
        </button>
    );
    if (to) return <Link to={to}>{btn}</Link>;
    return btn;
};

export const DeleteIconBtn = ({onClick, disabled}) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        style={{width: "28px", height: "28px", background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", opacity: disabled ? 0.5 : 1}}
    >
        <FiTrash2 size={13}/>
    </button>
);

export const CountBadge = ({value}) => (
    <span style={{background: "#eff6ff", color: "#2563eb", borderRadius: "999px", padding: "1px 8px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 700}}>
        {value}
    </span>
);

export const YesNoBadge = ({value}) => value
    ? <span style={{display: "inline-flex", alignItems: "center", gap: "4px", color: "#16a34a", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "6px", padding: "2px 8px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 600}}><FiCheck size={12}/> ANO</span>
    : <span style={{display: "inline-flex", alignItems: "center", gap: "4px", color: "#dc2626", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "6px", padding: "2px 8px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 600}}><FiMinus size={12}/> NE</span>;

export const StatusBadge = ({active, activeLabel = "Aktivní", inactiveLabel = "Neaktivní"}) => (
    <span style={{
        fontSize: "var(--enigoo-fs-xs)", fontWeight: 600, borderRadius: "5px", padding: "2px 8px",
        color:       active ? "#16a34a" : "#dc2626",
        background:  active ? "#f0fdf4" : "#fef2f2",
        border: `1px solid ${active ? "#bbf7d0" : "#fecaca"}`,
    }}>
        {active ? activeLabel : inactiveLabel}
    </span>
);

// ─── pagination bar ───────────────────────────────────────────────────────────

const getPageRange = (current, total) => {
    if (total <= 7) return Array.from({length: total}, (_, i) => i);
    const pages = [0];
    const lo = Math.max(1, current - 2);
    const hi = Math.min(total - 2, current + 2);
    if (lo > 1) pages.push("...");
    for (let i = lo; i <= hi; i++) pages.push(i);
    if (hi < total - 2) pages.push("...");
    pages.push(total - 1);
    return pages;
};

const PageBtn = ({label, active, disabled, onClick}) => {
    const [hov, setHov] = useState(false);
    const base = {width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "6px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", border: "1px solid #e2e8f0", transition: "background 0.12s", userSelect: "none"};
    const style = active
        ? {...base, background: "#2563eb", color: "#fff", border: "1px solid #2563eb"}
        : disabled
            ? {...base, background: "#fff", color: "#cbd5e1", opacity: 0.5}
            : {...base, background: hov ? "#f1f5f9" : "#fff", color: "#4b5563"};
    return (
        <button type="button" onClick={onClick} disabled={disabled} style={style}
            onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}>
            {label}
        </button>
    );
};

const TablePagination = ({page, totalPages, pageSize, total, onPage, onPageSize}) => (
    <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px", padding: "10px 14px", background: "#f8fafc", borderTop: "1px solid #e2e8f0", borderRadius: "0 0 8px 8px"}}>
        {/* left: info + page size */}
        <div style={{display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap"}}>
            <span style={{fontSize: "var(--enigoo-fs-xs)", color: "#94a3b8"}}>
                Zobrazeno{" "}
                <strong style={{color: "#475569"}}>{page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)}</strong>
                {" "}z{" "}
                <strong style={{color: "#475569"}}>{total}</strong>
            </span>
            <div style={{display: "flex", alignItems: "center", gap: "5px"}}>
                <span style={{fontSize: "var(--enigoo-fs-xs)", color: "#94a3b8"}}>Řádků:</span>
                <select
                    value={pageSize}
                    onChange={e => onPageSize(Number(e.target.value))}
                    style={{fontSize: "var(--enigoo-fs-sm)", padding: "3px 6px", border: "1px solid #e2e8f0", borderRadius: "6px", background: "#fff", color: "#1e293b", cursor: "pointer", outline: "none"}}
                >
                    {PAGE_SIZE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
            </div>
        </div>

        {/* right: numbered pages */}
        {totalPages > 1 && (
            <nav style={{display: "flex", alignItems: "center", gap: "4px"}}>
                <PageBtn label={<FiChevronLeft size={14}/>} disabled={page <= 0} onClick={() => onPage(page - 1)}/>
                {getPageRange(page, totalPages).map((p, i) =>
                    p === "..."
                        ? <span key={`e${i}`} style={{width: "32px", textAlign: "center", fontSize: "var(--enigoo-fs-sm)", color: "#94a3b8"}}>…</span>
                        : <PageBtn key={p} label={p + 1} active={p === page} onClick={() => onPage(p)}/>
                )}
                <PageBtn label={<FiChevronRight size={14}/>} disabled={page >= totalPages - 1} onClick={() => onPage(page + 1)}/>
            </nav>
        )}
    </div>
);

// ─── main table ───────────────────────────────────────────────────────────────

/**
 * columns: Array of { header, render(row, i), key?, center? }
 * renderRow(row, i): optional — overrides default <tr> (for expandable rows)
 * defaultPageSize: initial page size (default 100). Set to Infinity to disable pagination.
 */
const ETable = ({
    columns, data, emptyText, renderRow, defaultPageSize = 100,
    serverMode = false, total, page: pageProp, pageSize: pageSizeProp, onPage, onPageSize,
}) => {
    const [hoverRow, setHoverRow]           = useState(null);
    const [pageState, setPageState]         = useState(0);
    const [pageSizeState, setPageSizeState] = useState(defaultPageSize);

    // client-mode: reset na první stránku při změně dat; server-mode řídí parent
    useEffect(() => { if (!serverMode) setPageState(0); }, [data, serverMode]);

    if (!data || data.length === 0) {
        return (
            <div style={{borderRadius: "8px", border: "1px solid #e2e8f0"}}>
                <EmptyState text={emptyText}/>
            </div>
        );
    }

    const page       = serverMode ? pageProp : pageState;
    const pageSize   = serverMode ? pageSizeProp : pageSizeState;
    const totalCount = serverMode ? total : data.length;

    const totalPages  = pageSize === Infinity ? 1 : Math.ceil(totalCount / pageSize);
    const paginated   = serverMode || pageSize === Infinity ? data : data.slice(page * pageSize, (page + 1) * pageSize);
    const showPaging  = pageSize !== Infinity && totalCount > pageSize;

    const setPage        = serverMode ? onPage : setPageState;
    const handlePageSize = serverMode ? onPageSize : (newSize) => { setPageSizeState(newSize); setPageState(0); };

    return (
        <div style={{borderRadius: "8px", border: "1px solid #e2e8f0", overflow: "hidden"}}>
            <div style={{overflowX: "auto"}}>
                <table style={{width: "100%", borderCollapse: "collapse", background: "#fff"}}>
                    <thead>
                        <tr>
                            {columns.map((col, i) => (
                                <th key={i} style={{...thStyle, textAlign: col.center ? "center" : "left"}}>
                                    {col.header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {renderRow
                            ? paginated.map((row, i) => renderRow(row, page * pageSize + i))
                            : paginated.map((row, i) => {
                                const globalI = page * pageSize + i;
                                return (
                                    <tr
                                        key={row.id ?? globalI}
                                        onMouseEnter={() => setHoverRow(globalI)}
                                        onMouseLeave={() => setHoverRow(null)}
                                        style={{borderBottom: "1px solid #e2e8f0", background: hoverRow === globalI ? "#eff6ff" : globalI % 2 === 0 ? "#fff" : "#f8fafc"}}
                                    >
                                        {columns.map((col, j) => (
                                            <td key={j} style={{...tdStyle, textAlign: col.center ? "center" : "left"}}>
                                                {col.render ? col.render(row, globalI) : (col.key != null ? row[col.key] : null)}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })
                        }
                    </tbody>
                </table>
            </div>

            {showPaging && (
                <TablePagination
                    page={page} totalPages={totalPages} pageSize={pageSize} total={totalCount}
                    onPage={setPage} onPageSize={handlePageSize}
                />
            )}
        </div>
    );
};

export default ETable;
